import React, { useState } from 'react';
import { Tooltip, Typography, useTheme, TextField, Select, MenuItem } from '@mui/material';
import { CheckCircle, Error as ErrorIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { ContentCopy } from '@mui/icons-material';
import DOMPurify from 'dompurify';

interface InputFieldProps {
  extraClass?: string;
  label?: string;
  name?: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInput?: (e: React.FormEvent<HTMLInputElement>) => void;
  placeholder?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>, value: string | number) => void;
  type?: string;
  status?: 'success' | 'error'; // Optional validation status
  isDisabled?: boolean; // Disable the input field
  isMandatory?: boolean; // Show asterisk if the field is mandatory
  onBlur?: any;
  errorMessage?: string; // New prop for error message
  min?: number;
  max?: number;
  multiline?: boolean; // New prop for multiline support
  rows?: number; // Number of rows for multiline

  readOnly?: boolean;
  showCopyIcon?: boolean; // to optionally show copy icon
  onCopyClick?: () => void;
  showIncrementButtons?: boolean;
  showTimeUnitDropdown?: boolean; // New optional prop
  timeUnitOptions?: { label: string; value: string }[];
  timeUnitValue?: string;
  onTimeUnitChange?: (value: string) => void;
  autoComplete?: string;
}

const InputField: React.FC<InputFieldProps> = ({
  extraClass,
  label,
  name,
  value,
  onChange,
  onInput,
  placeholder = '',
  onKeyDown,
  type = 'text',
  status = null,
  isDisabled = false,
  isMandatory = false,
  onBlur,
  errorMessage = '', // Default empty string
  min,
  max,
  multiline = false,
  rows = 3,

  readOnly,
  showCopyIcon,
  onCopyClick,
  showIncrementButtons,
  showTimeUnitDropdown = false,
  timeUnitOptions = [
    { label: 'Years', value: 'years' },
    { label: 'Months', value: 'months' },
    { label: 'Days', value: 'days' },
  ],
  timeUnitValue,
  onTimeUnitChange,
  autoComplete,
}) => {
  const createSyntheticEvent = (newValue: string | number): React.ChangeEvent<HTMLInputElement> => {
    return {
      target: {
        name: name || '',
        value: newValue,
      },
    } as React.ChangeEvent<HTMLInputElement>;
  };
  const handleSanitizedChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const sanitizedValue = DOMPurify.sanitize(e.target.value, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    });
    
    onChange({
      target: {
        name: e.target.name,
        value: sanitizedValue,
      },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const theme = useTheme();
  const getIcon = () => {
    if (!value) return null; // Don't show icon if empty
    if (status === 'success') {
      return <CheckCircle sx={{ color: 'green', fontSize: 20 }} />;
    } else if (status === 'error') {
      return (
        <Tooltip title={errorMessage || 'Enter a valid Input'} arrow>
          <ErrorIcon sx={{ color: 'red', fontSize: 20 }} />
        </Tooltip>
      );
    }
    return null;
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev);
  };

  console.log(value, 'status', label, status, errorMessage);
  if (multiline) {
    return (
      <div className="flex flex-col px-[2px] w-full">
        <Typography
          variant="body2"
          fontWeight={600}
          color="textPrimary"
          className="self-start flex items-center space-x-1"
        >
          <span>{label}</span>
          {isMandatory && (
            <span className="text-red-500" title="Required field">
              *
            </span>
          )}
        </Typography>

        <TextField
          id={name}
          name={name}
          multiline
          rows={rows}
          value={value}
          onChange={handleSanitizedChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={isDisabled}
          error={status === 'error'}
          helperText={status === 'error' && errorMessage}
          variant="outlined"
          fullWidth
          className={`mt-2 p-3 text-black  border ${status === 'error' ? 'border-red-500' : 'border-gray-300'
            } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full ${isDisabled ? 'bg-gray-200 cursor-not-allowed' : ''
            } ${extraClass}`}
          InputProps={{
            style: {
              borderColor: theme.palette.action.selected,
              fontWeight: 400,
            },
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col px-[2px] w-full">
      {/* Label with optional mandatory asterisk */}
      <Typography
        variant="body2"
        fontWeight={600}
        color="textPrimary"
        className="self-start flex items-center space-x-1"
      >
        <span>{label}</span>
        {isMandatory && (
          <span className="text-red-500" title="Required field">
            *
          </span>
        )}
      </Typography>

      {/* Input field */}
      <div className="relative">
        <input
          id={name}
          name={name}
          type={type === 'password' && !isPasswordVisible ? 'password' : 'text'}
          value={value}
          autoComplete={autoComplete ?? (type === 'password' ? 'new-password' : 'on')}
          onKeyDown={(e) => onKeyDown?.(e, value)}
          onChange={readOnly ? undefined : handleSanitizedChange}
          onInput={onInput}
          onBlur={onBlur}
          placeholder={placeholder}
          style={{
            borderColor: theme.palette.action.selected,
          }}
          readOnly={readOnly}
          disabled={isDisabled}
          min={min}
          max={max}
          className={`mt-2 p-3 text-black border ${status === 'error' ? 'border-red-500' : 'border-gray-300'
            } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full ${isDisabled ? 'bg-gray-200 cursor-not-allowed' : ''
            } ${extraClass}`}
        />

        {/* Status icon */}
        {status !== null && value && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">{getIcon()}</div>
        )}

        {showCopyIcon && (
          <div
            className="absolute right-[5px] top-1/2 transform -translate-y-1/2 w-[40px] h-[40px] bg-white flex justify-center items-center cursor-pointer text-gray-500 hover:text-gray-700"
            onClick={onCopyClick}
            title="Copy to clipboard"
          >
            <ContentCopy sx={{ fontSize: 20 }} />
          </div>
        )}

        {/* Password visibility toggle */}
        {type === 'password' && (
          <div
            className="absolute right-10 top-1/2 transform -translate-y-1/2 cursor-pointer"
            onClick={togglePasswordVisibility}
          >
            {isPasswordVisible ? (
              <VisibilityOff sx={{ color: 'gray', fontSize: 20 }} />
            ) : (
              <Visibility sx={{ color: 'gray', fontSize: 20 }} />
            )}
          </div>
        )}

        {type === 'number' && showIncrementButtons && (
          <div className="absolute right-2 top-1/2 transform -translate-y-[35%] flex gap-2">
            <button
              type="button"
              className="text-xl"
              onClick={() => {
                const newVal = Number(value) - 1;
                const minimum = min ?? 0;
                if (newVal >= minimum) {
                  onChange(createSyntheticEvent(String(newVal)));
                }
              }}
            >
              -
            </button>
            <div className="h-8 w-0.5 bg-gray-200"></div>
            <button
              type="button"
              className="text-xl"
              onClick={() => {
                const newVal = Number(value) + 1;
                if (!max || newVal <= max) {
                  onChange(createSyntheticEvent(String(newVal)));
                }
              }}
            >
              +
            </button>
          </div>
        )}

        {showTimeUnitDropdown && (
          <Select
            value={timeUnitValue || 'years'}
            onChange={(e) => onTimeUnitChange && onTimeUnitChange(e.target.value)}
            size="small"
            variant="outlined"
            disabled={isDisabled}
            sx={{
              position: 'absolute',
              right: 2,
              top: 10,
              height: 'calc(100% - 12px)',
              borderRadius: '6px',
              minWidth: 120,
              fontSize: 14,
              backgroundColor: '#F2F2F2',
            }}
            renderValue={(selected) => {
              const selectedOption = timeUnitOptions.find((option) => option.value === selected);
              return selectedOption ? selectedOption.label : 'Years';
            }}
          >
            {timeUnitOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        )}
      </div>

      {/* Error message display */}
      {status === 'error' && errorMessage && (
        <Typography variant="caption" color="error" className="mt-1 flex items-center">
          <ErrorIcon sx={{ fontSize: 16, marginRight: 0.5 }} />
          {errorMessage}
        </Typography>
      )}
    </div>
  );
};

export default InputField;
